import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(3),
  },
  warningIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.error.main,
  },
  warningText: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  confirmName: {
    fontWeight: theme.typography.fontWeightBold,
  },
  deleteButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:disabled': {
      color: theme.palette.text.disabled,
      borderColor: theme.palette.text.disabled,
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
}));

export interface EpDevPConfirmDeleteContractDialogProps {
  contract: {
    contractname: string;
    uuid: string;
  };
  open: boolean;
  onCancel: () => void;
  onConfirmDelete: () => Promise<void>;
}

export const EpDevPConfirmDeleteContractDialog = ({
  contract,
  open,
  onCancel,
  onConfirmDelete,
}: EpDevPConfirmDeleteContractDialogProps) => {
  const { t } = useServiceRegistryTranslation();
  const classes = useStyles();
  const [confirmName, setConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmName('');
    }
  }, [open, contract.uuid]);

  const handleConfirmNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmName(event.target.value);
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      await onConfirmDelete();
    } finally {
      setIsDeleting(false);
      setConfirmName('');
    }
  };

  // Case sensitive comparison as per requirement H
  const isDeleteButtonEnabled = confirmName === contract.contractname;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>{t('contracts.delete')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="subtitle1" >
          {t('contracts.deleteNamedPrompt', { name: contract.contractname })}
        </Typography>
        <Typography variant="subtitle1">
          {t('contracts.typeNameToConfirm', { name: contract.contractname })}
        </Typography>
        <TextField
          className={classes.textField}
          variant="outlined"
          size="small"
          value={confirmName}
          onChange={handleConfirmNameChange}
          inputProps={{
            'aria-label': t('contracts.confirmName'),
          }}
          fullWidth
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onCancel} color="primary" disabled={isDeleting}>
          {t('contracts.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          //   className={classes.deleteButton}
          onClick={handleDeleteClick}
          disabled={!isDeleteButtonEnabled || isDeleting}
        >
          {isDeleting ? t('contracts.deleting') : t('contracts.deleteConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
